import React from 'react';
import { 
  Gavel, 
  School, 
  Shield, 
  Assignment, 
  AccountBalance,
  Security,
  Info,
  ContactMail
} from '@mui/icons-material';

const TermsConditions = () => {
  return (
    <section id="terms" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-blue-900 mb-4">Terms and Conditions</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Please read these terms and conditions carefully before using our services or enrolling at Moxie Master Public School
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Info className="text-blue-700 text-2xl" />
            <h3 className="text-blue-800 text-xl font-semibold">Effective Date</h3>
          </div>
          <p className="text-gray-600 leading-relaxed">
            These Terms and Conditions are effective as of January 1, 2025, and apply to all students, parents, guardians, 
            and visitors of Moxie Master Public School. By enrolling your child or accessing our services, you agree to be 
            bound by these terms.
          </p>
        </div>

        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <School className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">1. School Admission and Enrollment</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">1.1 Admission Requirements</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>All admission requirements must be met as outlined in our admissions policy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Students must meet age criteria specific to their grade level</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Complete and accurate information must be provided during application</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>All required documents must be submitted before enrollment confirmation</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">1.2 Enrollment Conditions</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Enrollment is subject to availability and successful completion of assessment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School reserves the right to refuse admission without stating reasons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Students may be placed on a waiting list if classes are full</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <AccountBalance className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">2. Fee Structure and Payment Terms</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">2.1 Fee Payment</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Annual fees must be paid in full at the time of enrollment or in agreed installments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Late payment fees of ₹500 per month will be charged after due date</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Fees are non-refundable except in cases specified in our refund policy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School reserves the right to revise fees with 30 days written notice</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">2.2 Additional Charges</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Additional charges may apply for optional activities, trips, and special programs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Damage to school property will result in repair or replacement charges</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <Assignment className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">3. Academic Policies and Student Conduct</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">3.1 Academic Standards</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Students are expected to maintain satisfactory academic progress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Regular attendance is mandatory and monitored closely</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Minimum 85% attendance required for promotion to next grade</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Academic honesty and integrity are strictly enforced</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">3.2 Code of Conduct</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Students must adhere to school's code of conduct and disciplinary policies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Bullying, harassment, or inappropriate behavior will not be tolerated</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Violation of conduct rules may result in suspension or expulsion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Uniform and dress code policies must be followed at all times</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">4. Health, Safety, and Security</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">4.1 Health Requirements</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Students must provide current medical certificates and vaccination records</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School must be notified of any medical conditions or allergies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Students with contagious illnesses must stay home to prevent spread</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">4.2 Safety and Security</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School maintains comprehensive safety and security measures</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Visitors must register at the main office and obtain visitor badges</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Emergency drills and procedures are conducted regularly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>CCTV monitoring is in place for security purposes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <Security className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">5. Privacy and Data Protection</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">5.1 Information Collection</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Personal information is collected only for educational and administrative purposes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Student records are maintained confidentially and securely</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Information may be shared with authorized educational authorities when required</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">5.2 Photography and Media</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School may photograph students for promotional and educational purposes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Parents may opt out of photography by providing written notice</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Images may be used in school publications, website, and social media</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <Gavel className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">6. Termination and Withdrawal</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">6.1 Voluntary Withdrawal</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Parents must provide 30 days written notice for student withdrawal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>All outstanding fees must be cleared before issuing transfer certificates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School property and library books must be returned</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">6.2 Dismissal by School</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School reserves the right to dismiss students for academic or disciplinary reasons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Non-payment of fees may result in suspension of services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Due process will be followed in all disciplinary matters</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <ContactMail className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">7. Liability and Disclaimers</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">7.1 Limitation of Liability</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School's liability is limited to the extent permitted by applicable law</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>Parents/guardians are responsible for their child's actions outside school premises</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School insurance covers accidents and incidents within school premises</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">7.2 Force Majeure</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>School is not liable for delays or failures due to circumstances beyond control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-800 mt-1">•</span>
                    <span>This includes natural disasters, pandemics, government regulations, or other emergencies</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center gap-3 mb-6">
              <Info className="text-blue-700 text-2xl" />
              <h3 className="text-blue-800 text-xl font-semibold">8. General Provisions</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">8.1 Amendments</h4>
                <p className="text-gray-600">
                  Moxie Master Public School reserves the right to modify these terms and conditions at any time. 
                  Changes will be communicated to parents/guardians through official school communication channels.
                </p>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">8.2 Governing Law</h4>
                <p className="text-gray-600">
                  These terms and conditions are governed by the laws of India and the jurisdiction of Pune courts.
                </p>
              </div>
              <div>
                <h4 className="text-blue-900 font-semibold mb-2">8.3 Severability</h4>
                <p className="text-gray-600">
                  If any provision of these terms is found to be invalid or unenforceable, the remaining provisions 
                  shall continue to be valid and enforceable.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-8 border-l-4 border-blue-800">
            <h3 className="text-blue-800 text-xl font-semibold mb-4">Contact Information</h3>
            <p className="text-gray-700 mb-4">
              For questions about these Terms and Conditions, please contact us:
            </p>
            <div className="space-y-2 text-gray-600">
              <p><strong>Moxie Master Public School</strong></p>
              <p>123 Education Street, Pune City, IN 411058</p>
              <p>Phone: +91 83590 87358</p>
              <p>Email: rohanrathi.dev@gmail.com</p>
              <p>Office Hours: Monday - Friday: 8:00 AM - 4:00 PM</p>
            </div>
          </div>

          <div className="text-center text-gray-500 text-sm mt-8">
            <p>Last updated: January 1, 2025</p>
            <p>© {new Date().getFullYear()} Moxie Master Public School. All rights reserved.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsConditions;